// src/LeaderboardPage.js
import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, orderBy, limit, getDocs, doc, getDoc } from 'firebase/firestore';
import { auth, firestore } from './firebase';
import { Trophy, Swords, TrendingUp, Search, RefreshCw, Crown, Zap, Shield } from 'lucide-react';
import { Avatar, getTier } from './battleUtils';
import Sidebar from './Sidebar';
import './LeaderboardPage.css';

// ─── Tier config ──────────────────────────────────────────────────────────────
const TIERS = [
  { min: 2000, label: 'Prompt Legend',    color: '#F59E0B', icon: '👑', gradient: 'linear-gradient(135deg, #F59E0B22, #F59E0B08)' },
  { min: 1800, label: 'Prompt Master',    color: '#8B5CF6', icon: '🔮', gradient: 'linear-gradient(135deg, #8B5CF622, #8B5CF608)' },
  { min: 1600, label: 'Prompt Architect', color: '#06B6D4', icon: '⚡', gradient: 'linear-gradient(135deg, #06B6D422, #06B6D408)' },
  { min: 1400, label: 'Prompt Engineer',  color: '#10B981', icon: '🛠',  gradient: 'linear-gradient(135deg, #10B98122, #10B98108)' },
  { min: 1200, label: 'Apprentice',       color: '#3B82F6', icon: '📖', gradient: 'linear-gradient(135deg, #3B82F622, #3B82F608)' },
  { min: 0,    label: 'Rookie',           color: '#6B7280', icon: '🌱', gradient: 'linear-gradient(135deg, #6B728022, #6B728008)' },
];

// ─── Seed leaderboard ─────────────────────────────────────────────────────────
// Keeps the board from looking empty pre-launch / pre-traffic. Same name pool
// as the server's ghost-bot list (BOT_NAMES in server.js) so a name a user
// actually battled shows up here too — feels consistent, not random filler.
// Static, client-side only: real users merge in and rank correctly alongside
// these, and as your real player base grows it naturally pushes these down
// and off the board. These don't move on their own — that's a separate job
// if you ever want it.
const SEED_BOTS = [
  { username: 'sora99',      elo: 2184, wins: 142, battles: 168 },
  { username: 'echo_kai',    elo: 2071, wins: 119, battles: 145 },
  { username: 'nitro99',     elo: 1968, wins: 103, battles: 134 },
  { username: 'prism99',     elo: 1887, wins: 95,  battles: 127 },
  { username: 'rex_prompt',  elo: 1842, wins: 88,  battles: 119 },
  { username: 'luna_dev',    elo: 1799, wins: 81,  battles: 112 },
  { username: 'hex_dev',     elo: 1763, wins: 76,  battles: 108 },
  { username: 'zara_x',      elo: 1721, wins: 70,  battles: 101 },
  { username: 'blaze42',     elo: 1688, wins: 65,  battles: 96  },
  { username: 'kai_writes',  elo: 1654, wins: 61,  battles: 92  },
  { username: 'swift_q',     elo: 1612, wins: 57,  battles: 87  },
  { username: 'neon_q',      elo: 1578, wins: 52,  battles: 81  },
  { username: 'dev_rishi',   elo: 1543, wins: 48,  battles: 77  },
  { username: 'mira_x',      elo: 1499, wins: 44,  battles: 72  },
  { username: 'arlo42',      elo: 1461, wins: 40,  battles: 68  },
  { username: 'pixel_dev',   elo: 1418, wins: 36,  battles: 63  },
  { username: 'jade_mn',     elo: 1382, wins: 33,  battles: 59  },
  { username: 'cruz42',      elo: 1347, wins: 29,  battles: 54  },
  { username: 'ash_dev',     elo: 1305, wins: 26,  battles: 50  },
  { username: 'nova_writes', elo: 1268, wins: 22,  battles: 45  },
  { username: 'finn_q',      elo: 1221, wins: 19,  battles: 41  },
  { username: 'leo_prompt',  elo: 1184, wins: 16,  battles: 36  },
  { username: 'alex_p',      elo: 1142, wins: 13,  battles: 32  },
  { username: 'maya92',      elo: 1097, wins: 10,  battles: 27  },
  { username: 'promptkid',   elo: 1043, wins: 7,   battles: 22  },
].map((b, i) => ({
  id:     `seed_${i}`,
  userId: `seed_${b.username}`,
  username: b.username,
  elo:    b.elo,
  wins:   b.wins,
  battles: b.battles,
}));

// ─── Rank medal ───────────────────────────────────────────────────────────────
const RankDisplay = ({ rank }) => {
  if (rank === 1) return <span className="lb-medal gold">🥇</span>;
  if (rank === 2) return <span className="lb-medal silver">🥈</span>;
  if (rank === 3) return <span className="lb-medal bronze">🥉</span>;
  return <span className="lb-rank-num">#{rank}</span>;
};

// ─── Top 3 podium cards ───────────────────────────────────────────────────────
const PodiumCard = ({ entry, rank, isMe }) => {
  const tier = getTier(entry.elo);
  const winRate = entry.battles > 0 ? Math.round((entry.wins / entry.battles) * 100) : 0;
  const heights = { 1: 'tall', 2: 'mid', 3: 'short' };

  return (
    <div className={`lb-podium-card rank-${rank} ${heights[rank]} ${isMe ? 'is-me' : ''}`}>
      <div className="lb-podium-glow" style={{ background: tier.color }} />
      <div className="lb-podium-rank">
        <RankDisplay rank={rank} />
      </div>
      <Avatar seed={entry.username} size={52} />
      <div className="lb-podium-name">{isMe ? `${entry.username} (You)` : entry.username}</div>
      <div className="lb-podium-tier" style={{ color: tier.color }}>
        {tier.icon} {tier.label}
      </div>
      <div className="lb-podium-elo" style={{ color: tier.color }}>{entry.elo}</div>
      <div className="lb-podium-sub">
        {entry.wins}W · {(entry.battles || 0) - (entry.wins || 0)}L · {winRate}%
      </div>
    </div>
  );
};

// ─── Table row ────────────────────────────────────────────────────────────────
const LeaderboardRow = ({ rank, entry, isMe }) => {
  const tier    = getTier(entry.elo);
  const winRate = entry.battles > 0 ? Math.round((entry.wins / entry.battles) * 100) : 0;
  const losses  = (entry.battles || 0) - (entry.wins || 0);

  return (
    <div
      className={`lb-row ${isMe ? 'is-me' : ''}`}
      style={isMe ? { background: tier.gradient } : {}}
    >
      <div className="lb-row-rank">
        <RankDisplay rank={rank} />
      </div>

      <div className="lb-row-player">
        <Avatar seed={entry.username} size={30} />
        <div className="lb-row-player-info">
          <span className="lb-row-username">
            {entry.username}
            {isMe && <span className="lb-you-badge">YOU</span>}
          </span>
          <span className="lb-row-tier" style={{ color: tier.color }}>
            {tier.icon} {tier.label}
          </span>
        </div>
      </div>

      <div className="lb-row-elo" style={{ color: tier.color }}>
        {entry.elo}
      </div>

      <div className="lb-row-winrate">
        <div className="lb-wr-bar">
          <div
            className="lb-wr-fill"
            style={{
              width: `${winRate}%`,
              background: winRate >= 60 ? '#10B981' : winRate >= 40 ? '#F59E0B' : '#EF4444'
            }}
          />
        </div>
        <span className="lb-wr-pct">{winRate}%</span>
      </div>

      <div className="lb-row-record">
        <span className="lb-wins">{entry.wins || 0}W</span>
        <span className="lb-sep">/</span>
        <span className="lb-losses">{losses}L</span>
      </div>

      <div className="lb-row-battles">
        <Swords size={12} />
        {entry.battles || 0}
      </div>
    </div>
  );
};

// ─── Tier guide sidebar ───────────────────────────────────────────────────────
const TierGuide = () => (
  <div className="lb-tier-guide">
    <div className="lb-tier-guide-title">
      <Shield size={14} />
      Rank Tiers
    </div>
    <div className="lb-tier-list">
      {TIERS.map(t => (
        <div key={t.label} className="lb-tier-row">
          <span className="lb-tier-icon">{t.icon}</span>
          <span className="lb-tier-label" style={{ color: t.color }}>{t.label}</span>
          <span className="lb-tier-elo">{t.min === 0 ? '< 1200' : `${t.min}+`}</span>
        </div>
      ))}
    </div>

    <div className="lb-elo-explainer">
      <div className="lb-elo-explainer-title"><TrendingUp size={13} /> How ELO works</div>
      <p>Win battles to gain ELO. Beating higher-ranked players gives more points. Starting ELO is 1000.</p>
    </div>
  </div>
);

// ─── My rank floating card ────────────────────────────────────────────────────
const MyRankCard = ({ myEntry, myRank }) => {
  if (!myEntry) return null;
  const tier    = getTier(myEntry.elo);
  const winRate = myEntry.battles > 0 ? Math.round((myEntry.wins / myEntry.battles) * 100) : 0;

  return (
    <div className="lb-my-card" style={{ borderColor: tier.color + '40' }}>
      <div className="lb-my-card-left">
        <Avatar seed={myEntry.username} size={36} />
        <div>
          <div className="lb-my-name">{myEntry.username}</div>
          <div className="lb-my-tier" style={{ color: tier.color }}>{tier.icon} {tier.label}</div>
        </div>
      </div>
      <div className="lb-my-stats">
        <div className="lb-my-stat">
          <span className="lb-my-stat-val" style={{ color: tier.color }}>{myEntry.elo}</span>
          <span className="lb-my-stat-label">ELO</span>
        </div>
        <div className="lb-my-stat">
          <span className="lb-my-stat-val">{myRank ? `#${myRank}` : '—'}</span>
          <span className="lb-my-stat-label">Rank</span>
        </div>
        <div className="lb-my-stat">
          <span className="lb-my-stat-val">{winRate}%</span>
          <span className="lb-my-stat-label">Win Rate</span>
        </div>
        <div className="lb-my-stat">
          <span className="lb-my-stat-val">{myEntry.battles || 0}</span>
          <span className="lb-my-stat-label">Battles</span>
        </div>
      </div>
    </div>
  );
};

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
const LeaderboardPage = () => {
  const [sidebarOpen, setSidebarOpen]   = useState(false);
  const [leaderboard, setLeaderboard]   = useState([]);
  const [loading, setLoading]           = useState(true);
  const [refreshing, setRefreshing]     = useState(false);
  const [myEntry, setMyEntry]           = useState(null);
  const [searchTerm, setSearchTerm]     = useState('');
  const [lastUpdated, setLastUpdated]   = useState(null);

  const user  = auth.currentUser;
  const myUid = user?.uid;

  const fetchData = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const q    = query(collection(firestore, 'battleLeaderboard'), orderBy('elo', 'desc'), limit(50));
      const snap = await getDocs(q);
      const live = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      // Merge real players in with the seed bots, re-sort by ELO, cap at 50.
      // Real users rank correctly among the seeds instead of replacing them.
      const merged = [...live, ...SEED_BOTS]
        .sort((a, b) => (b.elo || 0) - (a.elo || 0))
        .slice(0, 50);

      setLeaderboard(merged);
      setLastUpdated(new Date());

      if (myUid) {
        const mySnap = await getDoc(doc(firestore, 'battleLeaderboard', myUid));
        if (mySnap.exists()) setMyEntry(mySnap.data());
      }
    } catch (e) {
      console.error('Leaderboard fetch failed:', e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchData(); }, [myUid]);

  const myRank = useMemo(() => {
    if (!myUid || leaderboard.length === 0) return null;
    const idx = leaderboard.findIndex(e => e.userId === myUid);
    return idx >= 0 ? idx + 1 : null;
  }, [leaderboard, myUid]);

  const filtered = useMemo(() => {
    if (!searchTerm.trim()) return leaderboard;
    const q = searchTerm.toLowerCase();
    return leaderboard.filter(e => e.username?.toLowerCase().includes(q));
  }, [leaderboard, searchTerm]);

  const top3 = filtered.slice(0, 3);
  const rest = filtered.slice(3);

  const timeAgo = lastUpdated
    ? `${Math.round((Date.now() - lastUpdated.getTime()) / 1000)}s ago`
    : null;

  return (
    <div className="lb-shell">
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
      {sidebarOpen && <div className="lb-overlay" onClick={() => setSidebarOpen(false)} />}

      <div className={`lb-main ${sidebarOpen ? 'sidebar-open' : ''}`}>

        <button className="lb-hamburger" onClick={() => setSidebarOpen(v => !v)}>
          <span /><span /><span />
        </button>

        <div className="lb-header">
          <div className="lb-header-left">
            <div className="lb-header-icon"><Trophy size={20} /></div>
            <div>
              <h1>World Leaderboard</h1>
              <p className="lb-header-sub">
                Top {leaderboard.length} prompt engineers globally
                {timeAgo && <span> · Updated {timeAgo}</span>}
              </p>
            </div>
          </div>
          <div className="lb-header-right">
            <div className="lb-search-wrap">
              <Search size={13} />
              <input
                className="lb-search"
                placeholder="Search players…"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
            <button
              className={`lb-refresh-btn ${refreshing ? 'spinning' : ''}`}
              onClick={() => fetchData(true)}
              disabled={refreshing}
              title="Refresh"
            >
              <RefreshCw size={14} />
            </button>
          </div>
        </div>

        {myEntry && (
          <div className="lb-my-rank-wrap">
            <MyRankCard myEntry={myEntry} myRank={myRank} />
          </div>
        )}

        <div className="lb-content">
          <div className="lb-panel">
            {loading ? (
              <div className="lb-loading">
                <div className="lb-loading-spinner" />
                <span>Loading rankings…</span>
              </div>
            ) : filtered.length === 0 ? (
              <div className="lb-empty">
                <Trophy size={40} />
                <p>{searchTerm ? 'No players found' : 'No battles yet — be the first!'}</p>
              </div>
            ) : (
              <>
                {!searchTerm && top3.length >= 3 && (
                  <div className="lb-podium-section">
                    <div className="lb-podium-wrap">
                      <PodiumCard entry={top3[1]} rank={2} isMe={top3[1]?.userId === myUid} />
                      <PodiumCard entry={top3[0]} rank={1} isMe={top3[0]?.userId === myUid} />
                      {top3[2] && <PodiumCard entry={top3[2]} rank={3} isMe={top3[2]?.userId === myUid} />}
                    </div>
                  </div>
                )}

                <div className="lb-table">
                  <div className="lb-table-header">
                    <span>Rank</span>
                    <span>Player</span>
                    <span>ELO</span>
                    <span>Win Rate</span>
                    <span>Record</span>
                    <span>Battles</span>
                  </div>
                  <div className="lb-table-body">
                    {(searchTerm ? filtered : [...top3, ...rest]).map((entry) => {
                      const rank = leaderboard.findIndex(e => e.id === entry.id) + 1;
                      return (
                        <LeaderboardRow
                          key={entry.id}
                          rank={rank}
                          entry={entry}
                          isMe={entry.userId === myUid}
                        />
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="lb-side-panel">
            <TierGuide />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeaderboardPage;